import type {
  XCodeSample,
  XLanguageExample,
  XReadmeCodeSample,
} from '@scalar/workspace-store/schemas/extensions/operation'
import type { OperationObject } from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'

/** Turns a `{ language: source }` map (x-stainless-snippets) into code samples. */
const fromSnippetMap = (snippets: Record<string, string> | undefined, label?: string): XCodeSample[] =>
  Object.entries(snippets ?? {}).map(([lang, source]) => ({ lang, source, ...(label ? { label } : {}) }))

/**
 * Turns `x-stainless-examples` / `x-scalar-examples` into code samples.
 *
 * Each example carries per-language request snippets and an optional title that
 * we surface as the picker label. The response is only used for documentation,
 * so it is ignored here.
 */
const fromLanguageExamples = (examples: XLanguageExample | XLanguageExample[] | undefined): XCodeSample[] => {
  if (!examples) {
    return []
  }

  return (Array.isArray(examples) ? examples : [examples]).flatMap((example) =>
    fromSnippetMap(example.request, example.title),
  )
}

/** Turns ReadMe's `x-readme.code-samples` into code samples. */
const fromReadmeSamples = (samples: XReadmeCodeSample[] | undefined): XCodeSample[] =>
  (samples ?? []).map((sample) => ({
    lang: sample.language,
    source: sample.code,
    ...(sample.name ? { label: sample.name } : {}),
  }))

/** Picker group label for samples generated by an SDK provider (Scalar, Stainless, …). */
const SDK_GROUP_LABEL = 'SDK'

/** Picker group label for generic or docs-platform samples (the standard `x-codeSamples` family, ReadMe, …). */
export const CODE_EXAMPLES_GROUP_LABEL = 'Code Examples'

type CustomCodeSampleGroupLabel = typeof SDK_GROUP_LABEL | typeof CODE_EXAMPLES_GROUP_LABEL

type CustomCodeSamples = {
  /**
   * Group label for the code sample picker.
   *
   * Samples that come from an SDK provider extension (`x-scalar-examples`,
   * `x-stainless-snippets`, `x-stainless-examples`) surface under "SDK". Generic
   * or docs-platform samples (`x-readme`, the `x-codeSamples` family) surface
   * under "Code Examples".
   */
  label: CustomCodeSampleGroupLabel
  /** The custom code samples, in picker order. */
  samples: XCodeSample[]
}

/**
 * Grabs any custom code samples from the operation.
 *
 * Several tools write code samples into OpenAPI under different extensions. When
 * more than one is present we use the highest-priority source only, rather than
 * showing duplicates from every tool. Priority, highest first:
 *
 * 1. `x-scalar-examples` (SDK)
 * 2. `x-stainless-snippets` (SDK, overrides `x-stainless-examples`)
 * 3. `x-stainless-examples` (SDK)
 * 4. `x-readme.code-samples` (Code Examples)
 * 5. `x-codeSamples` / `x-code-samples` / `x-custom-examples` (Code Examples)
 *
 * The winning source also determines the picker group label: SDK-provider
 * extensions surface under "SDK", everything else under "Code Examples".
 *
 * Note that Speakeasy does not have its own extension — it writes into the
 * standard `x-codeSamples`, so its snippets surface under "Code Examples".
 *
 * @param operation - The operation to get the custom code samples from
 * @returns The picker group label and the custom code samples which exist in the operation
 */
export const getCustomCodeSamples = (operation: OperationObject): CustomCodeSamples => {
  const scalarExamples = operation['x-scalar-examples'] ?? []
  if (scalarExamples.length) {
    return { label: SDK_GROUP_LABEL, samples: scalarExamples }
  }

  const stainlessSnippets = fromSnippetMap(operation['x-stainless-snippets'])
  if (stainlessSnippets.length) {
    return { label: SDK_GROUP_LABEL, samples: stainlessSnippets }
  }

  const stainlessExamples = fromLanguageExamples(operation['x-stainless-examples'])
  if (stainlessExamples.length) {
    return { label: SDK_GROUP_LABEL, samples: stainlessExamples }
  }

  const readmeSamples = fromReadmeSamples(operation['x-readme']?.['code-samples'])
  if (readmeSamples.length) {
    return { label: CODE_EXAMPLES_GROUP_LABEL, samples: readmeSamples }
  }

  const customCodeKeys = ['x-custom-examples', 'x-codeSamples', 'x-code-samples'] as const
  return { label: CODE_EXAMPLES_GROUP_LABEL, samples: customCodeKeys.flatMap((key) => operation[key] ?? []) }
}
